import { useCallback, useEffect, useRef, useState } from 'react';

export function useCinematicSlides(
  total: number,
  autoDelay = 5200,
  stopAutoAt?: number,
  startAt = 0,
) {
  const [current, setCurrent] = useState(startAt);
  const [prev, setPrev] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const [mounted, setMounted] = useState(false);
  // Start in manualMode if we're not beginning at slide 0 (returning user),
  // or once the user navigates backward — disables auto-advance for the session.
  const [manualMode, setManualMode] = useState(startAt > 0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const goTo = useCallback(
    (next: number) => {
      if (transitioning || next === current || next < 0 || next >= total) return;
      clearTimeout(timerRef.current);
      setPrev(current);
      setCurrent(next);
      setTransitioning(true);
      setTimeout(() => {
        setPrev(null);
        setTransitioning(false);
      }, 700);
    },
    [current, transitioning, total],
  );

  const advance = useCallback(() => goTo(current + 1), [current, goTo]);

  // Backward nav → permanently disables auto-advance for this session.
  const retreat = useCallback(() => {
    setManualMode(true);
    goTo(current - 1);
  }, [current, goTo]);

  // Dot tap: going to an earlier slide also kills auto-advance.
  const goToManual = useCallback(
    (index: number) => {
      if (index < current) setManualMode(true);
      goTo(index);
    },
    [current, goTo],
  );

  useEffect(() => {
    if (manualMode) return;
    const stopAt = stopAutoAt ?? total - 1;
    if (current >= stopAt) return;
    timerRef.current = setTimeout(advance, autoDelay);
    return () => clearTimeout(timerRef.current);
  }, [current, advance, autoDelay, stopAutoAt, total, manualMode]);

  const slideClass = (index: number): string => {
    if (index === current) return `cin-slide${mounted ? ' cin-slide--active' : ''}`;
    if (index === prev) return 'cin-slide cin-slide--out';
    return 'cin-slide cin-slide--hidden';
  };

  return { current, mounted, slideClass, advance, retreat, goTo: goToManual };
}
